import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { utilisationTone, downloadBlob } from './format';

const thisMonthISO = () => new Date().toISOString().slice(0, 7);

const COLUMNS = ['Date', 'First Active', 'Last Active', 'Active', 'Idle', 'Away', 'Total', 'Active %'];

export default function MyHistory() {
    const [month, setMonth] = useState(thisMonthISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        axiosInstance.get('/api/working-time/me', { params: { month } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Could not load your timeline'))
            .finally(() => setLoading(false));
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const download = (format) => {
        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        axiosInstance
            .get('/api/working-time/me/export', {
                params: { month, format }, responseType: 'blob'
            })
            .then((r) => downloadBlob(r.data, `my-working-time-${month}.${extension}`))
            .catch(() => setError('Could not download the export'));
    };

    const summary = data && data.summary;

    return (
        <div>
            <div className="wt-toolbar">
                <div className="wt-field">
                    <label htmlFor="wt-my-month">Month</label>
                    <input
                        id="wt-my-month"
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    />
                </div>
                <button type="button" className="wt-btn" onClick={load}>Refresh</button>
                <button type="button" className="wt-btn primary" onClick={() => download('xlsx')}>
                    Excel
                </button>
                <button type="button" className="wt-btn" onClick={() => download('pdf')}>
                    PDF
                </button>
            </div>

            {error && <div className="wt-error">{error}</div>}

            {summary && (
                <div className="wt-stats">
                    <div className="wt-stat">
                        <div className="label">Days tracked</div>
                        <div className="value">{summary.daysTracked}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Active</div>
                        <div className="value">{summary.activeHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Idle</div>
                        <div className="value">{summary.idleHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div
                            className="label"
                            title="Time the agent was locked/off or otherwise not actively working."
                        >
                            Away
                        </div>
                        <div className="value">{summary.awayHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Total</div>
                        <div className="value">{summary.totalHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Avg active/day</div>
                        <div className="value">{summary.averageActive}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Active share</div>
                        <div className="value">{summary.utilisation}%</div>
                        <div className="hint">active vs total</div>
                    </div>
                </div>
            )}

            <div className="wt-table-wrap">
                <table className="wt-table">
                    <thead>
                        <tr>{COLUMNS.map((c) => <th key={c}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={COLUMNS.length} className="wt-empty">Loading…</td></tr>
                        )}
                        {!loading && !(data && data.timeline && data.timeline.length) && (
                            <tr>
                                <td colSpan={COLUMNS.length} className="wt-empty">
                                    No working time recorded for this month.
                                </td>
                            </tr>
                        )}
                        {!loading && data && data.timeline && data.timeline.map((d) => (
                            <tr key={d.qatarDay}>
                                <td>{d.qatarDay}</td>
                                <td className="wt-mono">{d.firstActiveTime || '-'}</td>
                                <td className="wt-mono">{d.lastActiveTime || '-'}</td>
                                <td className="wt-mono"><strong>{d.activeHours}</strong></td>
                                <td className="wt-mono">{d.idleHours}</td>
                                <td className="wt-mono">{d.awayHours}</td>
                                <td className="wt-mono"><strong>{d.totalHours}</strong></td>
                                <td>
                                    <span className={`wt-badge ${utilisationTone(d.utilisation)}`}>
                                        {d.utilisation}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
